import { useMemo } from "react";
import { useQuery } from "@apollo/client/react";
import { useAccessFeatures } from "@/auth/access-features";
import {
  AutoMatchApplicationStatesDocument,
  type ListingApplicationState,
} from "@/graphql/generated/operations";
import { applicationIdsOf, autoAddedMarks, type AutoAddedMark, type AutoAddition } from "./auto-match-runs";
import { useMatchAutoMatch } from "./use-match-auto-match";

/**
 * Die Markierungen der Zeilen, die automatische Läufe an ein gespeichertes Ergebnis angehängt haben.
 * Ohne angehängte Zeilen fragt der Hook nichts ab. Die Läufe kommen aus derselben Abfrage wie die
 * Laufliste, der Zustand der Bewerbungen nur für Eingetragene der Ausschreibung (`share.listings`).
 */
export function useAutoAddedMarks(
  matchId: string,
  additions: readonly AutoAddition[],
): ReadonlyMap<string, AutoAddedMark> {
  const none = additions.length === 0;
  const { runs } = useMatchAutoMatch(matchId, { skip: none });
  const { hasFeature } = useAccessFeatures();
  const applicationIds = useMemo(() => applicationIdsOf(additions, runs), [additions, runs]);
  const states = useQuery(AutoMatchApplicationStatesDocument, {
    variables: { input: { first: applicationIds.length, where: { id: { in: applicationIds } } } },
    skip: applicationIds.length === 0 || !hasFeature("share.listings"),
    // Wer die Bewerbungen nicht lesen darf, sieht keine Absage, aber auch keinen Fehler.
    errorPolicy: "ignore",
  });
  const nodes = states.data?.listingApplications.nodes;
  return useMemo(() => {
    const byId = new Map<string, ListingApplicationState>((nodes ?? []).map((node) => [node.id, node.state]));
    return autoAddedMarks(additions, runs, byId);
  }, [additions, nodes, runs]);
}
